import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ToastItem } from '../../helpers/types';
import { generateToastId, registerToastAction } from './toastActionRegistry';

// Queue-based toaster (WHEEL-315): toasts stack instead of overwriting each
// other; TosaterMui renders the whole queue. Oldest toasts are dropped
// beyond this cap so a burst of dispatches (e.g. bulk operations) can't
// flood the screen.
const MAX_TOASTS = 5;

export interface ToasterState {
  toasts: ToastItem[];
}

const initialState: ToasterState = {
  toasts: [],
};

interface ShowToastPayload {
  id: number;
  message: string;
  severity: string;
  duration?: number;
  link?: string;
  linkText?: string;
  actionLabel?: string;
}

const pushToast = (state: ToasterState, payload: ShowToastPayload) => {
  // Tolerate rehydrated legacy state (pre-queue single-slot shape).
  const existing = Array.isArray(state.toasts) ? state.toasts : [];
  const toast: ToastItem = {
    id: payload.id,
    message:
      typeof payload.message === 'string'
        ? payload.message
        : 'Something went wrong',
    severity: payload.severity,
    duration: payload.duration ?? 5000,
    link: payload.link || '',
    linkText: payload.linkText || '',
    actionLabel: payload.actionLabel || '',
  };
  // Drop duplicate message+severity toasts while one is visible — retry
  // storms shouldn't stack identical errors. Action toasts (e.g. two Undos
  // for two different threats) are never deduped.
  const isDuplicate =
    !toast.actionLabel &&
    existing.some(
      item =>
        !item.actionLabel &&
        item.message === toast.message &&
        item.severity === toast.severity,
    );
  if (isDuplicate) return;
  state.toasts = [...existing, toast].slice(-MAX_TOASTS);
};

const toasterSlice = createSlice({
  name: 'toaster',
  initialState,
  reducers: {
    showToast: {
      reducer: (state, action: PayloadAction<ShowToastPayload>) => {
        pushToast(state, action.payload);
      },
      // Preserves the legacy positional call signature used across the app:
      // showToast(message, severity, duration?, link?, linkText?)
      prepare: (
        message: string,
        severity: string,
        duration?: number,
        link?: string,
        linkText?: string,
      ) => ({
        payload: {
          id: generateToastId(),
          message,
          severity,
          duration,
          link,
          linkText,
        },
      }),
    },
    // Toast with an action button (e.g. Undo, WHEEL-316). The callback can't
    // live in redux state (non-serializable), so it's parked in the toast
    // action registry keyed by the toast id.
    showActionToast: {
      reducer: (state, action: PayloadAction<ShowToastPayload>) => {
        pushToast(state, action.payload);
      },
      prepare: (
        message: string,
        severity: string,
        actionLabel: string,
        onAction: () => void,
        duration?: number,
      ) => {
        const id = generateToastId();
        registerToastAction(id, onAction);
        return { payload: { id, message, severity, duration, actionLabel } };
      },
    },
    hideToast: {
      reducer: (state, action: PayloadAction<{ id?: number }>) => {
        const existing = Array.isArray(state.toasts) ? state.toasts : [];
        state.toasts =
          action.payload?.id === undefined
            ? []
            : existing.filter(toast => toast.id !== action.payload.id);
      },
      prepare: (id?: number) => ({ payload: { id } }),
    },
  },
});

export const { showToast, showActionToast, hideToast } = toasterSlice.actions;
export default toasterSlice.reducer;
